import { useState } from "react";
import { toast } from "sonner";
import { Trash2, RefreshCw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useGet } from "../lib/hooks";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Pill } from "./Common";

const STATUS = { connected: ["green", "Connected"], not_checked: ["slate", "Not checked"], not_resolving: ["amber", "Not pointing anywhere"], elsewhere: ["amber", "Points elsewhere"], error: ["red", "Error"] };



function Steps() {
  return (
    <div className="rounded-md border border-[#C2A462]/25 bg-[#16213a] p-5 text-sm text-slate-300" data-testid="domain-steps">
      <div className="mb-2 font-semibold text-[#EBD6A0]">How to connect a domain</div>
      <p>Contact BOS HQ Developer Co.</p>
    </div>
  );
}

function AddDomain({ accounts, reload }) {
  const [domain, setDomain] = useState("");
  const [acc, setAcc] = useState("");
  const add = async () => {
    try { await api.post("/agency/domains", { domain, account_id: acc || null }); toast.success("Domain recorded"); setDomain(""); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border bg-card p-4">
      <label className="flex-1 min-w-[220px] text-xs text-muted-foreground">Domain<Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="app.boshq.com.au" className="mt-1" data-testid="domain-input" /></label>
      <label className="min-w-[220px] text-xs text-muted-foreground">Used by
        <select value={acc} onChange={(e) => setAcc(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground" data-testid="domain-entity-select">
          <option value="">Enterprise (main BOS HQ login)</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </label>
      <Button onClick={add} disabled={!domain.trim()} data-testid="domain-add-btn">Record domain</Button>
    </div>
  );
}

export function CustomDomains({ accounts }) {
  const [list, reload] = useGet("/agency/domains");
  const [busy, setBusy] = useState(null);
  const name = (id) => (id ? accounts.find((a) => a.id === id)?.name || "Entity" : "Enterprise");
  const check = async (d) => {
    setBusy(d.id);
    try { const { data } = await api.post(`/agency/domains/${d.id}/check`); toast[data.status === "connected" ? "success" : "message"](STATUS[data.status]?.[1] || data.status); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const remove = async (d) => { if (!window.confirm(`Remove ${d.domain} from this list? (This doesn't change your DNS.)`)) return; await api.delete(`/agency/domains/${d.id}`); reload(); };
  return (
    <div className="space-y-5" data-testid="custom-domains">
      <Steps />
      <AddDomain accounts={accounts} reload={reload} />
      {list && !list.length && <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">No domains recorded yet.</div>}
      {list?.map((d) => (
        <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-4" data-testid={`domain-row-${d.id}`}>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-sm">{d.domain}</div>
            <div className="text-xs text-muted-foreground">{name(d.account_id)}{d.checked_at ? ` · checked ${d.checked_at.slice(0, 16).replace("T", " ")}` : ""}</div>
            {d.detail && <div className="mt-1 text-xs text-slate-400" data-testid={`domain-detail-${d.id}`}>{d.detail}</div>}
          </div>
          <Pill tone={STATUS[d.status]?.[0] || "slate"} testid={`domain-status-${d.id}`}>{STATUS[d.status]?.[1] || d.status}</Pill>
          <Button size="sm" variant="secondary" disabled={busy === d.id} onClick={() => check(d)} data-testid={`domain-check-${d.id}`}><RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy === d.id ? "animate-spin" : ""}`} />Check connection</Button>
          <Button size="icon" variant="ghost" onClick={() => remove(d)} data-testid={`domain-delete-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
        </div>
      ))}
    </div>
  );
}
